/**
 * Lekki współdzielony klient publicznego katalogu.
 *
 * Pamięta tylko małe, JSON-owe wyniki i deduplikuje identyczne requesty.
 * Ostatnia poprawna wartość jest używana wyłącznie przy błędach transportowych
 * (429/5xx/timeout/network), nigdy zamiast prawdziwego 404.
 */

const SUCCESS_TTL_MS = 15_000;
const LAST_GOOD_MAX_AGE_MS = 10 * 60_000;

interface SuccessEntry<T> {
  value: T;
  expiresAt: number;
}

interface LastGoodEntry<T> {
  value: T;
  savedAt: number;
}

export class CatalogRequestError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = "CatalogRequestError";
    this.status = status;
  }
}

const successCache = new Map<string, SuccessEntry<unknown>>();
const lastGoodCache = new Map<string, LastGoodEntry<unknown>>();
const inFlight = new Map<string, Promise<unknown>>();

export interface CatalogRequestOptions<T> {
  ttlMs?: number;
  /** Wartość poprawnego 404, np. null dla nieistniejącej kolekcji. */
  onNotFound?: () => T;
}

export async function fetchCatalogResource<T>(
  key: string,
  url: string,
  options: CatalogRequestOptions<T> = {},
): Promise<T> {
  const now = Date.now();
  const cached = successCache.get(key) as SuccessEntry<T> | undefined;
  if (cached) {
    if (cached.expiresAt > now) return cached.value;
    successCache.delete(key);
  }

  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const request = (async (): Promise<T> => {
    try {
      const response = await fetch(url);
      if (response.status === 404 && options.onNotFound) {
        const value = options.onNotFound();
        remember(key, value, options.ttlMs ?? SUCCESS_TTL_MS);
        return value;
      }
      if (!response.ok) {
        throw new CatalogRequestError(`Catalog request failed (${response.status})`, response.status);
      }
      const value = (await response.json()) as T;
      if (!isCompleteCatalogValue(value)) {
        throw new CatalogRequestError("Catalog response was incomplete");
      }
      remember(key, value, options.ttlMs ?? SUCCESS_TTL_MS);
      return value;
    } catch (error) {
      const stale = lastGoodCache.get(key) as LastGoodEntry<T> | undefined;
      if (stale && Date.now() - stale.savedAt <= LAST_GOOD_MAX_AGE_MS) {
        return stale.value;
      }
      throw error;
    }
  })();

  inFlight.set(key, request);
  try {
    return await request;
  } finally {
    inFlight.delete(key);
  }
}

function isCompleteCatalogValue(value: unknown): boolean {
  if (value === undefined || value === null || typeof value !== "object") return false;
  if (Array.isArray(value) || Object.keys(value).length === 0) return false;
  const record = value as Record<string, unknown>;
  if ("products" in record && !Array.isArray(record.products)) return false;
  if ("items" in record && !Array.isArray(record.items)) return false;
  if (
    "collection" in record &&
    (!record.collection || typeof record.collection !== "object")
  ) {
    return false;
  }
  if ("product" in record && (!record.product || typeof record.product !== "object")) {
    return false;
  }
  return true;
}

function remember<T>(key: string, value: T, ttlMs: number): void {
  successCache.set(key, { value, expiresAt: Date.now() + ttlMs });
  lastGoodCache.set(key, { value, savedAt: Date.now() });
}

/** Wyłącznie dla testów jednostkowych klienta katalogu. */
export function resetCatalogDataCacheForTests(): void {
  successCache.clear();
  lastGoodCache.clear();
  inFlight.clear();
}